import React, { useState, useRef } from 'react'
import { useMutation } from "@tanstack/react-query";
import FlipMove from "react-flip-move";
import Discipline from "../../../models/Discipline";
import ProfileItemForm from "./ProfileItemForm";
import ItemSelectModal from "../../../components/ItemSelectModal";
import TalentAverageBill from './TalentAverageBill';
import { Modal, Input, Alert } from "antd";
import Form, { FormInputComponent } from "../../../components/form/Form";
import TalentCandidate from '../../../models/TalentCandidate';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useAuth } from '../../../providers/AuthProvider';
import User from '../../../models/User';
import AIIcon from "../../../components/AiIcon";
import AIResumeScoreModal from "./AIResumeScoreModal";
import NEWSkillFormButton from '../../profiles/components/NEWSkillFormButton';


const SparkleIcon = () => {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z" fill="#7c3aed" stroke="#7c3aed" strokeWidth="1" strokeLinejoin="round" />
            <path d="M19 14L19.54 16.73L22 17L19.54 17.27L19 20L18.46 17.27L16 17L18.46 16.73L19 14Z" fill="#a78bfa" stroke="#a78bfa" strokeWidth="0.5" strokeLinejoin="round" />
            <path d="M5 3L5.36 4.82L7 5L5.36 5.18L5 7L4.64 5.18L3 5L4.64 4.82L5 3Z" fill="#a78bfa" stroke="#a78bfa" strokeWidth="0.5" strokeLinejoin="round" />
        </svg>
    );
}

const ChevronIcon = ({ open }) => {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
             style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

const ScorePill = ({ score, open, onClick }) => {
    const outOf10 = (score / 10).toFixed(1);
    return (
        <button
            onClick={onClick}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 transition-all"
        >
            <SparkleIcon />
            <span className="text-xs text-violet-500 font-medium">Resume Score</span>
            <span className="text-xs font-bold text-violet-700">
                {outOf10}<span className="font-normal text-violet-400">/10</span>
            </span>
            <ChevronIcon open={open} />
        </button>
    );
}


const SubScoreBar = ({ label, value }) => {
    const pct = (value / 10) * 100;
    const barColor = value >= 8 ? "bg-emerald-500"
            : value >= 6 ? "bg-amber-400"
                : "bg-gray-300";

    return (
        <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-32 shrink-0">{label}</span>
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }} />
            </div>
            <span className={`text-xs font-semibold w-6 text-right "text-gray-700"`}>
                {`${value}`}
            </span>
        </div>
    );
}

const RedFlagBanner = ({ penalty }) => {
    if (!penalty || penalty === 0) return null;
    return (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <p className="text-xs text-red-600 font-medium leading-relaxed">
                Red flags detected — Review your resume for inconsistencies or missing information.
            </p>
        </div>
    );
}
const RoleHeaderWithBreakdown = ({discipline, disciplines, onScoreSuccess, ...props}) => {
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const scoreModalRef = useRef();
    const { pros, cons, subscores } = discipline.resume_match_breakdown || {pros: [], cons: [], subscores: {}};
    const hasPros = pros.length > 0;
    const hasCons = cons.length > 0;
    const SUBSCORE_LABELS = {
        title_match: "Title Match",
        skills_depth: "Skills Depth",
        experience_relevance: "Experience",
        resume_quality: "Resume Quality",
        red_flags_penalty: "Red Flags",
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row items-start justify-between">
                <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                        Select skills and experience level for:
                    </p>
                    <h2 className="text-lg font-semibold text-violet-700">{discipline.name}</h2>
                </div>
                <div className="flex items-center gap-2">
                    {discipline.resume_match_breakdown && (
                        <ScorePill score={discipline.resume_match_score} open={breakdownOpen} onClick={() => setBreakdownOpen(!breakdownOpen)} />
                    )}
                    {!discipline.resume_match_breakdown && (
                        <div className="flex items-center gap-2">
                            <p className="text-xs">Missing Resume Score</p>
                            <button
                                type="button"
                                onClick={() => scoreModalRef.current?.open()}
                                className="btn btn-dark py-1 pl-1 pr-2.5 flex gap-1 rounded-full"
                            >
                                <AIIcon fill="#fff" />
                                <span className="mt-px">
                                    Generate Score
                                </span>
                            </button>
                            <AIResumeScoreModal
                                ref={scoreModalRef}
                                disciplines={disciplines || [discipline]}
                                onSuccess={onScoreSuccess}
                            />
                        </div>
                    )}
                    {props.children}
                </div>
            </div>


            {/* Expandable Breakdown */}
            {discipline.resume_match_breakdown && (
                <div style={{ maxHeight: breakdownOpen ? 600 : 0, opacity: breakdownOpen ? 1 : 0, overflow: "hidden", transition: "max-height 0.35s ease, opacity 0.25s ease" }}>
                    <div className="mx--20 mb-2 rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 px-3 py-2.5 space-y-4">

                        {/* Subscores */}
                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Score Breakdown</p>
                            <div className="space-y-2">
                                {Object.entries(subscores).map(([key, val]) => key === "red_flags_penalty" ? null : (
                                    <SubScoreBar key={key} label={SUBSCORE_LABELS[key]} value={val} />
                                ))}
                            </div>
                        </div>
                        {/* Red flag banner */}
                        <RedFlagBanner penalty={subscores.red_flags_penalty} />

                        {/* Pros & Cons — conditionally rendered */}
                        {(hasPros || hasCons) && (
                            <div className={`grid gap-3 ${hasPros && hasCons ? "grid-cols-2" : "grid-cols-1"}`}>
                                {hasPros && (
                                    <div>
                                        <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                                            Strengths
                                        </p>
                                        <ul className="space-y-1.5">
                                            {pros.map((p, i) => (
                                                <li key={i} className="text-xs text-gray-600 leading-relaxed flex gap-1.5">
                                                    <span className="text-emerald-400 mt-0.5 shrink-0">•</span>{p}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {hasCons && (
                                    <div>
                                        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                            Gaps
                                        </p>
                                        <ul className="space-y-1.5">
                                            {cons.map((c, i) => (
                                                <li key={i} className="text-xs text-gray-600 leading-relaxed flex gap-1.5">
                                                    <span className="text-amber-400 mt-0.5 shrink-0">•</span>{c}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* No pros no cons — perfect score note */}
                        {!hasPros && !hasCons && (
                            <p className="text-xs text-gray-500 italic">No specific strengths or gaps identified — resume aligns strongly across all evaluated dimensions.</p>
                        )}

                        <p className="text-xs text-gray-400 flex items-center gap-1.5 pt-1">
                            <SparkleIcon />
                            AI-generated analysis based on your uploaded resume
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default class TalentDisciplineForm extends ProfileItemForm {

    static ItemType = 'Role'

    static FormClass = 'talent-role-form add-talent-form '

    constructor(props) {
        super(props);

        let model = this.getModelFromProps();
        let importedItem = false;
        // if (!props.user?.isApplicationApproved()) {
        importedItem = props.user.importedItemFromResume('talent_disciplines', props.model)
        // }
        this.formRef = React.createRef();

        this.state = {
            model,
            importedItem
        }
    }


    getModelFromProps() {
        let { self_score, hourly_rate, ...discipline } = TB.tryObject(this.props.model);

        let selected_skill_ids = [];
        if (!TB.keys(TB.tryObject(discipline)).length) return {};
        if (discipline?.id && this.props.skills?.length) {

            let disciplineSkills = Discipline.skillsFor(discipline?.id);
            let selectedSkills = TB.map(this.props.skills || [], 'id');
            selected_skill_ids = TB.filter(TB.map(disciplineSkills || [], 'id'), skillId => selectedSkills?.includes(skillId));
        }
        return { discipline, self_score, hourly_rate, selected_skill_ids }
    }

    /**
     * Handles form change
     *
     * @returns void
     */
    onChange(model) {

        // removing default hourly rate - jira PATDVB-9
        // if (model.discipline && !model.hourly_rate) {
        //     const platformDisciplineRate = TB.max(
        //         TB.map(model.discipline ? [model.discipline] : disciplines, dis => UserDiscipline.rateForDiscipline(dis.id)
        //         ))

        //     model.hourly_rate = platformDisciplineRate;
        // }

        if (!model.discipline?.id) {
            delete model.hourly_rate;
            delete model.self_score;
        } else if (model.discipline?.id && this.getModelFromProps().discipline?.id !== model.discipline.id) {

            let disciplineIds = TB.map((this.props.disciplines || []), 'id');

            if (this.getModelFromProps().discipline?.id) {
                disciplineIds = TB.reject(disciplineIds, this.getModelFromProps().discipline?.id);
            }

            if (disciplineIds.length && disciplineIds.includes(model.discipline.id)) {

                this.formRef.current.updateInputsWithError({
                    discipline: ''
                });

                this.formRef.current.forceValidate();
                // return this.setState({model: this.getModelFromProps()})
            }
        }

        if (model.discipline?.id && Discipline.skillsFor(model?.discipline?.id)?.length) {
            let disciplineSkills = Discipline.skillsFor(model.discipline?.id);
            let selectedSkills = TB.map(this.props.skills || [], 'id');
            model.selected_skill_ids = TB.filter(TB.map(disciplineSkills || [], 'id'), skillId => selectedSkills?.includes(skillId));

        }

        return this.setState({ model })
    }
    /**
     * Handles form submit button clicks when form is valid
     *
     * @returns void
     */
    async onSubmit() {
        let { self_score, hourly_rate, discipline, selected_skill_ids } = {
            ...this.getModelFromProps(),
            ...this.state.model
        };

        if (Discipline.skillsFor(discipline?.id)?.length && !selected_skill_ids?.length) {
            await TB.alert('error', 'Skills For Role Required',
                'At least one relevant skill must be added for this role. Please choose a matching skill and try again.',
                [
                    { title: 'Ok' },
                ]
            )
            return;
        }
        let confirm = true;
        if (this.props.user?.isApplicationApproved()) {
            confirm = await TB.alert('warning', 'Role requires approval',
                'A Wripple Talent Manager will need to review and approve new roles before they can be added to your profile. You can check back here to see the status of the approval. A team member may reach out via email if there are any questions. Would you like to continue?',
                [
                    { title: 'Yes, Continue' },
                    { title: 'Cancel' }
                ]
            )

            if (!confirm) {
                return;
            }
        }





        hourly_rate = Number.parseFloat(hourly_rate);

        let { name, id } = TB.tryObject(discipline);

        return super.onSubmit({
            self_score,
            hourly_rate,
            name, id
        });
    }

    getSkillsForDiscipline() {
        if (!TB.isDefined(this.state.model?.discipline?.id)) {
            return [];
        }
        return Discipline.skillsFor(this.state.model?.discipline.id);
    }


    /**
     * The fields mapping for the form
     */
    formFields = () => {
        let {
            discipline,
            self_score,
        } = this.state.model;
        let hasDisciplineSelected = TB.isDefined(discipline?.id);

        const isSupplier = this.props.user.isTalentSupplierResource();

        let positionFields = [
            {
                style: styles.fieldSection,
                fields: {
                    discipline: {
                        type: 'search',
                        searchType: 'disciplines',
                        placeholder: TB.trans('talent.talent_discipline_form.discipline_placeholder'),
                        valueLabelDisplay: 'name',
                        disabled: this.props.disabled,
                        label: `${laravel.platform_name} Role`,
                        required: true,
                        searchRequestEndpoint: '/api/talent-disciplines/search',
                        columns: 6,
                        style: styles.field,
                        validations: {
                            roleNotSelected: ({ discipline }, value) => {
                                if (!TB.isDefined(value?.id)) {
                                    return TB.trans('talent.talent_discipline_form.discipline_rule_role_not_selected')
                                }

                                return true;
                            },
                            roleAlreadySelected: ({ discipline }, value) => {

                                let disciplineIds = TB.map((this.props.disciplines || []), 'id');

                                if (this.getModelFromProps().discipline?.id) {
                                    disciplineIds = TB.filter(disciplineIds, id => id !== this.getModelFromProps().discipline?.id);
                                }
                                if (TB.isDefined(value?.id) && disciplineIds.length && disciplineIds.includes(value.id)) {
                                    return "Role already selected";
                                }

                                return true;
                            }
                        },
                        appendAfter: !hasDisciplineSelected && (
                            <p className="btn-form-modal" onClick={() => this.showModal()}>
                                {TB.trans('talent.talent_discipline_form.view_role_descriptions')}
                            </p>
                        )
                    },
                    self_score: {
                        type: 'select',
                        label: TB.trans('talent.talent_discipline_form.self_score_label'),
                        required: true,
                        disabled: this.props.disabled,
                        hideVisibility: !hasDisciplineSelected,
                        options: laravel.talent_scoring_options,
                        placeholder: TB.trans('talent.talent_discipline_form.self_score_placeholder'),
                        style: styles.field,
                        columns: 3,
                    },

                }
            }
        ];

        if (!isSupplier || this.props.isAdminEditing) {
            positionFields[0].fields.hourly_rate = {
                label: TB.trans('talent.talent_discipline_form.hourly_rate_label'),
                type: 'number',
                addonBefore: "$",
                placeholder: TB.trans('talent.talent_discipline_form.hourly_rate_placeholder'),
                required: true,
                disabled: this.props.disabled,
                hideVisibility: !hasDisciplineSelected,
                validationErrors: {
                    isDefaultRequiredValue: TB.trans('talent.talent_discipline_form.hourly_rate_validation_errors.missing_hourly_rate'),
                },
                style: styles.field,
                columns: 3
            }
        }
        // hourly_rate: {
        //     label: TB.trans('talent.talent_discipline_form.hourly_rate_label'),
        //     type: 'number',
        //     addonBefore: "$",
        //     placeholder: TB.trans('talent.talent_discipline_form.hourly_rate_placeholder'),
        //     required: true,
        //     disabled: this.props.disabled,
        //     hideVisibility: !hasDisciplineSelected,
        //     validationErrors: {
        //         isDefaultRequiredValue: TB.trans('talent.talent_discipline_form.hourly_rate_validation_errors.missing_hourly_rate'),
        //     },
        //     style: styles.field,
        //     columns: 3
        // },


        if (this.props.isEditing) {

            let disciplineSkills = this.getSkillsForDiscipline()
            let skills = disciplineSkills;

            if (hasDisciplineSelected) {

                let userSkills = TB.keyBy(this.props.user.skills, 'id');

                skills = TB.map(skills, (skill) => {
                    return userSkills[skill.id] || skill
                });
            }


            positionFields.push(
                {
                    style: styles.fieldSection,
                    fields: {
                        skills: {
                            component: (discipline?.name && skills.length > 0) ? (
                                <>
                                    {(!isSupplier || this.props.isAdminEditing) && <TalentAverageBill discipline={discipline} highlighted={false} />}
                                    <div className={"form-group mt-3"} style={styles.field}>
                                        <div className="form-title-section">
                                            <div className="step-title-content p-0">
                                                <div>
                                                    <h5 className="form-subtitle mb-3">
                                                        <span>
                                                            Choose a least one skill and rate your experience or expertise level for your role:
                                                            <span className="text-primary ml-2">
                                                                {discipline.name}
                                                            </span>
                                                        </span>
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={"discipline-skills-container mt-1 gap-3"}>
                                            <FlipMove typeName={null}>
                                                {TB.orderBy(skills, [({ self_score }) => !TB.isNil(self_score), 'pivot.display_index'], ['desc', 'asc'])
                                                    .map((skill, index) => (
                                                        <div key={`discipline-${discipline.id}-skill-${skill.id}`}>
                                                            <NEWSkillFormButton
                                                                disabled={this.props.disabled}
                                                                skill={skill}
                                                                className={skill.category && "min-w-[165px]"}
                                                                scoringOptions={skill.category ? laravel.talent_scoring_options_by_category[skill.category] : laravel.talent_scoring_options}
                                                                type={skill.category ? 'info' : 'default'}
                                                                onChange={(skill) => this.selectSkill(skill)}
                                                            />
                                                        </div>
                                                    ))}
                                            </FlipMove>
                                        </div>
                                        <div className="form-title-section">
                                            <div className="step-title-content p-0">
                                                <div>
                                                    <h5 className="form-subtitle mt-3">
                                                        <span>
                                                            Don't see a skill you are looking for? You can add more skills later.
                                                        </span>
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                disciplineSkills?.length > 0 ? (
                                    <div className={"form-group mt-3"} style={styles.field}>
                                        <div className={"help-element mt-2"}>
                                            <p>{TB.trans('talent.talent_discipline_form.skills_placeholder')}</p>
                                        </div>
                                    </div>
                                ) : <div />
                            ),
                        },
                    }
                },

            );
        }
        return positionFields
    };

    showModal() {
        return TB.event("Modal@show", {
            title: 'Browse categories and select a role you want to play',
            content: (
                <ItemSelectModal
                    fieldName="disciplines"
                    selectText="Select Role"
                    onSelectItem={async (discipline) => {
                        await this.formRef.current.updateInputsWithValue({
                            discipline
                        })
                        await TB.event("Modal@hide", true);
                    }}
                    selectedIds={TB.map(this.props.user.talent_disciplines || [], 'id')}
                    multiSelect={false}
                    isDisabled={false}
                    categoryHeader="Role Category"
                    hideModal={() => TB.event("Modal@hide", true)}
                    itemHeader={`${laravel.platform_name} Role`}
                    items={[...laravel.discipline_categories]}
                />
            )
        })
    }

    /***********************************
     * Update Project Position
     ************************************/
    async selectSkill(model) {

        let user = TB.pick({ ...this.props.user }, ['skills']);
        if (!user.skills) {
            user.skills = [];
        }
        let skillIndex = TB.findIndex(user.skills, ['id', model.id]);

        if (skillIndex !== -1) {
            if (!TB.isNil(model.self_score)) {
                user = TB.set(user, `skills[${skillIndex}]`, model);
            } else {
                TB.unset(user, `skills[${skillIndex}]`);
                // console.log('unset')
            }
        } else {
            user.skills.push({ ...model })
        }

        user.skills = TB.filter(user.skills, s => !TB.isNil(s?.id))

        await this.props.onChange(user);
        if (this.state.model?.discipline?.id && Discipline.skillsFor(this.state.model?.discipline?.id)?.length) {
            let disciplineSkills = Discipline.skillsFor(this.state.model?.discipline?.id);
            let selectedSkills = TB.map(user.skills || [], 'id');
            this.setState({
                model: {
                    ...this.state.model,
                    selected_skill_ids: TB.filter(
                        TB.map(disciplineSkills || [], 'id'),
                        skillId => selectedSkills?.includes(skillId)
                    )
                }
            })
        }
    }

    renderTitle() {
        let name = this.state.model?.discipline?.name;
        const { discipline } = this.state.model;

        let disc = discipline;

        if (discipline?.latest_appeal) {
            disc = { ...discipline.latest_appeal, discipline_id: discipline.id };
        }

        let status;
        if (!disc?.is_approved && !disc?.is_rejected) { // pending
            status = {
                className: 'bg-yellow-100 text-yellow-800',
                title: 'Pending Approval',
            }
        } else if (disc?.is_rejected) {
            status = {
                className: 'bg-red-100 text-red-800',
                title: 'Not Approved',
            }
        } else if (disc?.is_approved) {
            status = {
                className: 'bg-green-100 text-green-800',
                title: 'Approved',
            }
        }

        return (
            <div className="form-title-section">
                <div className="step-title-content w-full px-0">
                    <RoleHeaderWithBreakdown
                        discipline={this.props.model}
                        disciplines={this.props.disciplines?.map(d => this.props.user?.talent_disciplines?.find(td => td.id === d.id) || d) || [this.props.model]}
                        onScoreSuccess={(data) => {
                            if (this.props.onChange) {
                                this.props.onChange({ ...this.props.user, ...data });
                            }
                        }}
                    >
                        <div className="flex-none text-right flex flex-col items-end">
                            {(!this.props.user?.isTalentSupplierResource() && this.props.user?.isApplicationApproved()) ? (
                                <React.Fragment>
                                    {discipline && (
                                        <div className="flex gap-2">
                                            {disc?.is_rejected && (
                                                <AppealModal
                                                    discipline={disc}
                                                    disciplineName={discipline.name}
                                                    isAppeal={!!discipline?.latest_appeal}
                                                    onChange={(updates) => this.setState({ model: { ...this.state.model, ...updates } })}
                                                />
                                            )}

                                            <p className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded ${status.className}`}>{status.title}</p>
                                        </div>
                                    )}

                                    {(!disc?.is_approved && !disc?.is_rejected) && discipline?.latest_appeal && (
                                        <p className="text-xxs text-gray-500 py-2">Appeal submitted: {TB.moment(disc?.created_at).format('ll')}</p>
                                    )}
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {!!this.state.importedItem && (
                                        <div className="flex gap-2">
                                            <p className={`flex flex-row items-center gap-0.5 text-xs font-medium pr-1 pl-2 py-0.5 rounded bg-purple-100 text-purple-800`}>

                                                Imported From Resume
                                                <AIIcon />
                                            </p>
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </RoleHeaderWithBreakdown>
                </div>
            </div>
        )
    }


    /**
     * Show info on bottom of panel
     *
     * @returns {div}
     */
    renderFormFooterInfo() {
        let { discipline } = this.state.model;

        if (!this.getSkillsForDiscipline()?.length) {
            return null;
        }
        let selectedSkills = this.props.user?.selectedSkillsForDiscipline(discipline?.id) || [];

        return (
            <div className="flex flex-row items-center order-1 px-6">
                {selectedSkills?.length === 0 ? (
                    <p className="text-danger text-xs">
                        <b>0</b> skills selected. Please edit the role and add at least one skill to continue.
                    </p>
                ) : (
                    <p className="text-xs text-primary">
                        <b>{selectedSkills?.length}</b> {TB.pluralize('skill', selectedSkills?.length || 0)} selected. Edit the role to view or add more.
                    </p>
                )}
            </div>
        )
    }

    /**
     * Edit button for form
     *
     * @returns {div}
     */
    renderEditFormButton() {
        return (
            <button
                className="btn btn-primary px-6"
                disabled={this.props.disabled}
                type="button" onClick={() => this.props.onEditing(true)}>
                Edit Role/Skills
            </button>
        );
    }

}

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
};


const AppealModal = ({ discipline, disciplineName, isAppeal, onChange }) => {
    const { auth, update: updateAuth } = useAuth();
    const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
    const [showAppealForm, setShowAppealForm] = useState(false);

    const handleClose = () => {
        setIsAppealModalOpen(false);
        setShowAppealForm(false);
    }

    const addTalentRoleAppealMutation = useMutation(['applicationRoleAppeal'],
        params => TalentCandidate.addTalentRoleAppeal((discipline.discipline_id || discipline.id), params),
        {
            onSuccess: async data => {
                handleClose();

                const talent_disciplines = auth.talent_disciplines.map(dis => {
                    if (dis.id === (discipline.discipline_id || discipline.id)) {
                        return {
                            ...dis,
                            latest_appeal: data.appeal,
                        }
                    }
                    return dis
                });

                const updatedUser = User.make({ ...auth, talent_disciplines });
                const didUpdate = await updateAuth(updatedUser);

                if (didUpdate) {
                    let updatedDiscipline = TB.find(talent_disciplines, ['id', (discipline.discipline_id || discipline.id)]);
                    onChange({ discipline: updatedDiscipline });

                    TB.alert('success', 'Role Appeal Submitted', 'A talent manager will review your appeal and will reach out via email.')
                }
            },
            onError: err => {
                console.log(err, 'ERRRORR')
            }
        }
    )


    return (
        <>
            <button
                type="button"
                className="btn p-0 min-w-[unset] py-0"
                onClick={() => setIsAppealModalOpen(true)}
            >
                <i className="fa fa-info-circle text-black text-sm" />
            </button>
            <Modal
                title={`${isAppeal ? 'Appeal' : 'Role'} Not Approved for ${disciplineName}`}
                closable={true}
                maskClosable={true}
                style={{ top: 150 }}
                destroyOnClose={true}
                width={500}
                footer={null}
                open={isAppealModalOpen}
                onCancel={handleClose}
                onClose={handleClose}
            >
                {addTalentRoleAppealMutation.isLoading && (
                    <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center z-10">
                        <LoadingSpinner size="lg" />
                    </div>
                )}
                {showAppealForm ? (
                    <Form
                        showRequired
                        background={'transparent'}
                        title={null}
                        subtitle={null}
                        submitButton={false}
                        className="[&>.form-container]:p-0"
                        style={{ padding: 20 }}
                        onSubmit={async fields => addTalentRoleAppealMutation.mutateAsync(fields)}
                        isDisabled={addTalentRoleAppealMutation.isLoading}
                        fields={[]}
                    >
                        <div>
                            <label htmlFor="appeal-reason" className="block pb-1">
                                Write a note to explain your reason for appeal. After your appeal is submitted, a Wripple Talent Manager will review and reach out via email if there are any questions.
                            </label>

                            <FormInputComponent
                                fieldName="notes"
                                disabled={addTalentRoleAppealMutation.isLoading}
                                required
                                label="Notes"
                                type="antd-input"
                            >
                                <Input.TextArea
                                    showCount
                                    bordered
                                    id="appeal-reason"
                                    autoSize={{ minRows: 4 }}
                                    maxLength={500}
                                />
                            </FormInputComponent>
                        </div>
                        <div className="flex flex-row justify-between items-center gap-3">
                            <div className="mr-auto">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setShowAppealForm(false)}
                                >
                                    Back
                                </button>

                                {addTalentRoleAppealMutation.failureReason && (
                                    <p className="text-danger mr-auto">{addTalentRoleAppealMutation.failureReason?.message}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                disabled={addTalentRoleAppealMutation.isLoading}
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={addTalentRoleAppealMutation.isLoading}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <div>
                            <p>
                                This {isAppeal ? 'appeal' : 'role'} is not approved at this time.
                                Further feedback from the Wripple Talent Team has been provided below.
                                {!isAppeal ? ' If you believe this decision is incorrect, you may request an appeal at any time using the button below.' : ''}
                            </p>

                            <Alert
                                className="mt-4"
                                description={(
                                    <p>
                                        Feedback: {discipline.rejection_notes}
                                    </p>
                                )}
                                type="warning"
                            />
                        </div>
                        <div className="pt-5 flex flex-row justify-between gap-3">
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => {
                                    if (discipline.can_submit_appeal) {
                                        setShowAppealForm(true)
                                    } else {
                                        handleClose();
                                        TB.alert('warning', "Can't resubmit appeal at this time", 'Your last appeal was not approved. You can resubmit an appeal 1 year from the date it was not approved if you still disagree with this decision.')
                                    }
                                }}
                            >
                                Submit Appeal
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </>
    )
}